import { ClientHints } from "types/analytics";

/**
 * User-Agent Client Hints (`navigator.userAgentData`).
 *
 * This is the most trustworthy source for the Chromium version when available:
 * the brand list carries the real major version even on browsers that have
 * frozen or reduced their UA string, and the high-entropy hints add the full
 * version and the hardware model.
 *
 * Two caveats matter on the plotters:
 *  - It requires a secure context. The helm is served over plain HTTP from the
 *    unit, so this is expected to be unavailable there and the user agent string
 *    stays the primary source.
 *  - It only exists from Chromium 90, so old plotters never have it.
 */

const HIGH_ENTROPY_HINTS = [
  "architecture",
  "bitness",
  "model",
  "platformVersion",
  "uaFullVersion",
  "fullVersionList",
];

const unavailable: ClientHints = {
  available: false,
  brands: null,
  mobile: null,
  platform: null,
  architecture: null,
  bitness: null,
  model: null,
  platform_version: null,
  ua_full_version: null,
  full_version_list: null,
};

/** Refused or unknown hints come back as empty strings; treat those as absent */
const nonEmpty = (value: any): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const toBrandList = (value: any) =>
  Array.isArray(value)
    ? value.map((entry) => ({
        brand: String(entry?.brand ?? ""),
        version: String(entry?.version ?? ""),
      }))
    : null;

/**
 * Picks the Chromium version out of a brand list, ignoring the deliberately
 * randomised "greased" brand entries the spec requires browsers to include.
 */
export const chromiumVersionFromHints = (
  hints: ClientHints | null
): string | null => {
  if (!hints) return null;

  const lists = [hints.full_version_list, hints.brands];

  for (let i = 0; i < lists.length; i++) {
    const list = lists[i];
    if (!list) continue;

    for (let j = 0; j < list.length; j++) {
      if (/^chromium$/i.test(list[j].brand) && list[j].version) {
        return list[j].version;
      }
    }
  }

  return null;
};

/**
 * Resolves the client hints. Always resolves — an unsupported or non-secure
 * context yields `available: false` rather than an error.
 */
export const requestClientHints = async (): Promise<ClientHints> => {
  const data: any = (window.navigator as any)?.userAgentData;
  if (!data) return unavailable;

  const hints: ClientHints = {
    ...unavailable,
    available: true,
    brands: toBrandList(data.brands),
    mobile: typeof data.mobile === "boolean" ? data.mobile : null,
    platform: nonEmpty(data.platform),
  };

  if (typeof data.getHighEntropyValues !== "function") return hints;

  try {
    const high = await data.getHighEntropyValues(HIGH_ENTROPY_HINTS);

    return {
      ...hints,
      architecture: nonEmpty(high.architecture),
      bitness: nonEmpty(high.bitness),
      model: nonEmpty(high.model),
      platform_version: nonEmpty(high.platformVersion),
      ua_full_version: nonEmpty(high.uaFullVersion),
      full_version_list: toBrandList(high.fullVersionList),
    };
  } catch (e) {
    // Individual hints can be refused; the low-entropy ones are still useful
    return hints;
  }
};
